import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import OuraNav from "../components/OuraNav";
import OuraSecondNav from "../components/OuraSecondNav";
import fetchBase from "../utils/fetchBase";

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    rectSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableHubTile({ hub, renamingId, renameValue, setRenamingId, setRenameValue, handleRenameHub, handleDeleteHub, navigate }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: hub.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: "#fff",
        borderRadius: "14px",
        padding: "20px",
        border: "1px solid #e0e0e0",
        cursor: "grab",
        position: "relative",
        boxShadow: isDragging ? "0 8px 24px rgba(0,0,0,0.18)" : "0 1px 4px rgba(0,0,0,0.06)",
        transition: "box-shadow 0.2s",
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={() => !isDragging && navigate(`/document-hub/${hub.id}`)}
        >
            <div style={{ fontSize: "28px", marginBottom: "10px" }}>🗂</div>
            {renamingId === hub.id ? (
                <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: "6px", marginTop: "6px" }}>
                    <input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleRenameHub(hub.id)}
                        onPointerDown={e => e.stopPropagation()}
                        style={{ flex: 1, padding: "4px 8px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "13px" }}
                    />
                    <button onPointerDown={e => e.stopPropagation()} onClick={() => handleRenameHub(hub.id)} style={{ background: "#1d1d1f", color: "#fff", border: "none", borderRadius: "6px", padding: "4px 8px", fontSize: "12px", cursor: "pointer" }}>Save</button>
                    <button onPointerDown={e => e.stopPropagation()} onClick={() => setRenamingId(null)} style={{ background: "none", border: "1px solid #ccc", borderRadius: "6px", padding: "4px 8px", fontSize: "12px", cursor: "pointer" }}>✕</button>
                </div>
            ) : (
                <p
                    style={{ fontWeight: "600", fontSize: "14px", color: "#1d1d1f", margin: 0 }}
                    onPointerDown={e => e.stopPropagation()}
                    onDoubleClick={e => { e.stopPropagation(); setRenamingId(hub.id); setRenameValue(hub.name); }}
                >
                    {hub.name}
                </p>
            )}
            <button
                onPointerDown={e => e.stopPropagation()}
                onClick={(e) => handleDeleteHub(e, hub.id)}
                style={{
                    position: "absolute", top: "10px", right: "10px",
                    background: "none", border: "none", color: "#ff3b30",
                    fontSize: "13px", cursor: "pointer", fontWeight: "700"
                }}
            >
                ✕
            </button>
        </div>
    );
}

export default function DocumentHubPage() {
    const [hubs, setHubs] = useState([]);
    const [newHubName, setNewHubName] = useState("");
    const [showInput, setShowInput] = useState(false);
    const [createMode, setCreateMode] = useState("scratch");
    const [copySourceId, setCopySourceId] = useState("");
    const [loading, setLoading] = useState(true);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [members, setMembers] = useState({ employees: [], clients: [] });
    const navigate = useNavigate();


    const token = localStorage.getItem("jwt_token");
    const authHeader = `Bearer ${token}`;

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

        const fetchHubs = async () => {
            try {
                const res = await fetch(`${fetchBase}/api/document-hub/hubs`, {
                    headers: { Authorization: authHeader }
                });
                const data = await res.json();
                if (Array.isArray(data)) setHubs(data);
            } catch (err) {
                console.error("Failed to fetch hubs:", err);
            } finally {
                setLoading(false);
            }
        };

        const fetchMembers = async () => {
            try {
                const res = await fetch(`${fetchBase}/api/document-hub/members`, {
                    headers: { Authorization: authHeader }
                });
                const data = await res.json();
                setMembers({
                    employees: Array.isArray(data.employees) ? data.employees : [],
                    clients: Array.isArray(data.clients) ? data.clients : []
                });
            } catch (err) {
                console.error("Failed to fetch members:", err);
            }
        };

        const handleMemberDelete = async (virtualHubId, memberName) => {
            if (!window.confirm(`Delete all assigned records for ${memberName}? This cannot be undone.`)) return;
            try {
                await fetch(`${fetchBase}/api/document-hub/members/${virtualHubId}`, {
                    method: "DELETE",
                    headers: { Authorization: authHeader }
                });
                fetchMembers();
            } catch (err) {
                console.error("Failed to delete member:", err);
            }
        };



        const handleDragEnd = (event) => {
            const { active, over } = event;
            if (active.id !== over?.id) {
                setHubs(items => {
                    const oldIndex = items.findIndex(h => h.id === active.id);
                    const newIndex = items.findIndex(h => h.id === over.id);
                    return arrayMove(items, oldIndex, newIndex);
                });
            }
        };

        useEffect(() => {
            fetchHubs();
            fetchMembers();
        }, []);


    const handleCreateHub = async () => {
            if (!newHubName.trim()) return;
            try {
                const url = createMode === "copy" && copySourceId
                    ? `${fetchBase}/api/document-hub/hubs/${copySourceId}/copy`
                    : `${fetchBase}/api/document-hub/hubs`;
                const res = await fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader
                    },
                    body: JSON.stringify({ name: newHubName.trim() })
                });
                if (res.ok) {
                    setNewHubName("");
                    setShowInput(false);
                    setCreateMode("scratch");
                    setCopySourceId("");
                    fetchHubs();
                }
            } catch (err) {
                console.error("Failed to create hub:", err);
            }
        };

    const handleRenameHub = async (id) => {
            if (!renameValue.trim()) return;
            try {
                const res = await fetch(`${fetchBase}/api/document-hub/hubs/${id}/rename`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader
                    },
                    body: JSON.stringify({ name: renameValue.trim() })
                });
                if (res.ok) {
                    setRenamingId(null);
                    setRenameValue("");
                    fetchHubs();
                }
            } catch (err) {
                console.error("Failed to rename hub:", err);
            }
        };

        const handleDeleteHub = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm("Delete this Document Hub and all its contents?")) return;
        try {
            await fetch(`${fetchBase}/api/document-hub/hubs/${id}`, {
                method: "DELETE",
                headers: { Authorization: authHeader }
            });
            fetchHubs();
        } catch (err) {
            console.error("Failed to delete hub:", err);
        }
    };

    return (
        <div style={{ backgroundColor: "#f2f2f7", minHeight: "100vh" }}>
            <OuraNav
                links={[
                    { label: "Dashboard", href: "/admin-dashboard" },
                    { label: "Employees", href: "/employees" },
                    { label: "Clients", href: "/admin/clients" },
                    { label: "Scheduler", href: "/scheduler" },
                    { label: "Payroll", href: "/admin/payroll" }
                ]}
            />
            <OuraSecondNav
                mode="members"
                members={members}
                onMemberClick={(virtualHubId) => navigate(`/document-hub/${virtualHubId}`)}
                onMemberDelete={handleMemberDelete}
            />


            <div style={{ maxWidth: 700, margin: "0 auto", padding: "88px 20px 40px" }}>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                    <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#1d1d1f", margin: 0 }}>Document Hub</h1>
                    <button
                        onClick={() => setShowInput(true)}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: "#1d1d1f",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer"
                        }}
                    >
                        + New Hub
                    </button>
                </div>

                {showInput && (
                    <div style={{
                        background: "#fff",
                        borderRadius: "12px",
                        padding: "16px",
                        marginBottom: "20px",
                        border: "1px solid #e0e0e0",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px"
                    }}>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <button
                                onClick={() => setCreateMode("scratch")}
                                style={{
                                    flex: 1, padding: "8px", borderRadius: "6px", fontSize: "13px", cursor: "pointer",
                                    backgroundColor: createMode === "scratch" ? "#1d1d1f" : "#f2f2f7",
                                    color: createMode === "scratch" ? "#fff" : "#1d1d1f",
                                    border: "1px solid #e0e0e0", fontWeight: "600"
                                }}
                            >
                                Create from Scratch
                            </button>
                            <button
                                onClick={() => setCreateMode("copy")}
                                style={{
                                    flex: 1, padding: "8px", borderRadius: "6px", fontSize: "13px", cursor: "pointer",
                                    backgroundColor: createMode === "copy" ? "#1d1d1f" : "#f2f2f7",
                                    color: createMode === "copy" ? "#fff" : "#1d1d1f",
                                    border: "1px solid #e0e0e0", fontWeight: "600"
                                }}
                            >
                                Copy from Existing
                            </button>
                        </div>

                        {createMode === "copy" && (
                            <select
                                value={copySourceId}
                                onChange={e => setCopySourceId(e.target.value)}
                                style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "14px", color: "#1d1d1f" }}
                            >
                                <option value="">Select a hub to copy from</option>
                                {hubs.map(h => (
                                    <option key={h.id} value={h.id}>{h.name}</option>
                                ))}
                            </select>
                        )}

                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                            <input
                                type="text"
                                placeholder="New hub name e.g. State Client Binder"
                                value={newHubName}
                                onChange={(e) => setNewHubName(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleCreateHub()}
                                autoFocus
                                style={{
                                    flex: 1, padding: "8px 12px", borderRadius: "6px",
                                    border: "1px solid #ccc", fontSize: "14px",
                                    outline: "none", color: "#1d1d1f", backgroundColor: "#ffffff"
                                }}
                            />
                            <button
                                onClick={handleCreateHub}
                                style={{ padding: "8px 16px", backgroundColor: "#1d1d1f", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", cursor: "pointer" }}
                            >
                                Create
                            </button>
                            <button
                                onClick={() => { setShowInput(false); setNewHubName(""); setCreateMode("scratch"); setCopySourceId(""); }}
                                style={{ padding: "8px 12px", backgroundColor: "transparent", color: "#6e6e73", border: "1px solid #ccc", borderRadius: "6px", fontSize: "13px", cursor: "pointer" }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {loading ? (
                    <p style={{ color: "#6e6e73", fontSize: "14px" }}>Loading...</p>
                ) : hubs.length === 0 ? (
                    <p style={{ color: "#6e6e73", fontSize: "14px" }}>No document hubs yet. Create one to get started.</p>
                ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                          <SortableContext items={hubs.map(h => h.id)} strategy={rectSortingStrategy}>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
                                  {hubs.map(hub => (
                                      <SortableHubTile
                                          key={hub.id}
                                          hub={hub}
                                          renamingId={renamingId}
                                          renameValue={renameValue}
                                          setRenamingId={setRenamingId}
                                          setRenameValue={setRenameValue}
                                          handleRenameHub={handleRenameHub}
                                          handleDeleteHub={handleDeleteHub}
                                          navigate={navigate}
                                      />
                                  ))}
                              </div>
                          </SortableContext>
                      </DndContext>
                )}
            </div>
        </div>
    );
}